import React, { useMemo } from 'react';
import styled from 'styled-components';

import type { IWeekDay } from '@/types/calendar';

import { classList } from '@/helpers';
import { otherColors } from '@/styles/colors';
import { media } from '@/styles/theme';
import {
  HOUR_IN_PX,
  LINE_IN_PX,
  TIMELINE_COLUMN_WIDTH,
  TIMELINE_COLUMN_WIDTH_MOBILE,
  Z_INDEX_TIMELINE_COLUMN,
  Z_INDEX_TIMELINE_COLUMN_SHADOW_COVER,
  Z_INDEX_TIMELINE_HOURS,
} from '@/config/variables';
import {
  CurrentTimeIndicator,
  LoaderWrapper,
  StickyShadow,
  TimelineColumnSkeleton,
  TimelineHours,
} from '@/components/common';
import { CalendarDisplayRange } from '@/store/enums';

interface ITimelineColumn {
  daysToDisplay: IWeekDay[];
  isCalendarMessageVisible: boolean;
  selectedDays: CalendarDisplayRange;
  ignoreDLS: boolean | undefined;
}

const TimelineColumn = ({
  daysToDisplay,
  isCalendarMessageVisible,
  selectedDays,
  ignoreDLS,
}: ITimelineColumn): React.ReactElement => {
  const isLoading = daysToDisplay.length === 0;

  const isTzVisible = useMemo(
    () => !ignoreDLS && selectedDays === CalendarDisplayRange.ONE_DAY && daysToDisplay?.[0]?.isDlsDay,
    [daysToDisplay, ignoreDLS, selectedDays],
  );

  return (
    <>
      <Container>
        <LoaderWrapper className="h-100 px-2" isLoading={isLoading} loader={<TimelineColumnSkeleton />}>
          <StickyShadow.Placeholder isCalendarMessageDisplayed={isCalendarMessageVisible} />
          <StickyShadow />
          <div className="timeline-container">
            {!isLoading && <StickyShadow.Cover />}
            <TimelineHours day={daysToDisplay[0]} />
          </div>
          <CurrentTimeIndicator isOnTimeline={true} isTzVisible={isTzVisible} />
        </LoaderWrapper>
      </Container>
      {!isLoading && (
        <>
          <StickyShadow.Cover
            className={classList('timeline-column-shadow-cover', daysToDisplay[0]?.isCurrentDay && 'current-day')}
          />
        </>
      )}
    </>
  );
};

export { TimelineColumn };

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: ${TIMELINE_COLUMN_WIDTH_MOBILE}px;
  min-width: ${TIMELINE_COLUMN_WIDTH_MOBILE}px;
  font-variant-numeric: tabular-nums;
  position: sticky;
  z-index: ${Z_INDEX_TIMELINE_COLUMN};
  left: 0;
  background-color: #fff;

  .timeline-container {
    position: relative;
    align-self: stretch;
    transition: padding-right 0.2s linear;

    .timeline-hours {
      position: relative;
      z-index: ${Z_INDEX_TIMELINE_HOURS};
    }
  }

  transition: min-width 0.2s linear, width 0.2s linear;

  & + .timeline-column-shadow {
    left: ${TIMELINE_COLUMN_WIDTH_MOBILE}px;
    top: 90px;
    background-image: linear-gradient(90deg, rgba(0, 0, 0, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
    margin-left: -4px;
    width: 4px;
    height: 100%;

    transition: left 0.2s linear, top 0.2s linear;

    & + .timeline-column-shadow-cover {
      left: ${TIMELINE_COLUMN_WIDTH_MOBILE}px;
      top: 90px;
      z-index: ${Z_INDEX_TIMELINE_COLUMN_SHADOW_COVER};
      width: 4px;
      height: calc(100% - 90px);

      background-color: #fff;
      background-position: 0 4px;
      background-image: repeating-linear-gradient(
        180deg,
        ${otherColors.calendar.hGrid},
        ${otherColors.calendar.hGrid} ${LINE_IN_PX}px,
        transparent ${LINE_IN_PX}px,
        transparent ${HOUR_IN_PX}px
      );

      transition: left 0.2s linear, top 0.2s linear;

      &.current-day {
        background-color: ${otherColors.calendar.currentDay};
        background-image: repeating-linear-gradient(
          180deg,
          ${otherColors.calendar.currentDayHGrid},
          ${otherColors.calendar.currentDayHGrid} ${LINE_IN_PX}px,
          transparent ${LINE_IN_PX}px,
          transparent ${HOUR_IN_PX}px
        );
      }

      &.dls-day {
        background-color: ${otherColors.calendar.dlsDay};
        background-image: repeating-linear-gradient(
          180deg,
          ${otherColors.calendar.currentDayHGrid},
          ${otherColors.calendar.currentDayHGrid} ${LINE_IN_PX}px,
          transparent ${LINE_IN_PX}px,
          transparent ${HOUR_IN_PX}px
        );
      }
    }
  }

  ${media.tablet} {
    min-width: ${TIMELINE_COLUMN_WIDTH}px;
    width: ${TIMELINE_COLUMN_WIDTH}px;

    & + .timeline-column-shadow {
      left: ${TIMELINE_COLUMN_WIDTH}px;
      top: 143px;

      & + .timeline-column-shadow-cover {
        left: ${TIMELINE_COLUMN_WIDTH}px;
        top: 143px;
      }
    }
  }
`;
